import { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import api from "@/lib/api";

const WINDOWS = [
  { key: "7", label: "Last 7 days" },
  { key: "30", label: "Last 30 days" },
  { key: "90", label: "Last 90 days" },
];

function BreakdownTable({ title, counts, testid }) {
  const rows = Object.entries(counts || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="ae-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">{title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-600">No signups in this window.</div>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {rows.map(([name, n]) => (
              <tr key={name} className="border-t border-white/5 first:border-t-0">
                <td className="py-1.5 text-slate-300 font-mono text-xs">{name}</td>
                <td className="py-1.5 text-right text-emerald-400 font-mono tabular-nums">{n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function WindowPanel({ data, windowKey }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid={`attribution-window-${windowKey}`}>
      <div className="grid grid-cols-3 gap-4">
        <div className="ae-card p-5 text-center" data-testid={`attribution-total-${windowKey}`}>
          <div className="font-heading text-3xl font-bold text-slate-100 font-mono tabular-nums">{data.total_signups}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1.5">Total Signups</div>
        </div>
        <div className="ae-card p-5 text-center" data-testid={`attribution-activated-${windowKey}`}>
          <div className="font-heading text-3xl font-bold text-emerald-400 font-mono tabular-nums">{data.activated_users}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1.5">Activated (saved an opportunity)</div>
        </div>
        <div className="ae-card p-5 text-center" data-testid={`attribution-paid-${windowKey}`}>
          <div className="font-heading text-3xl font-bold text-emerald-400 font-mono tabular-nums">{data.paid_users}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1.5">Paid Users</div>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <BreakdownTable title="By UTM Source" counts={data.by_utm_source} testid={`attribution-by-source-${windowKey}`} />
        <BreakdownTable title="By UTM Medium" counts={data.by_utm_medium} testid={`attribution-by-medium-${windowKey}`} />
        <BreakdownTable title="By UTM Campaign" counts={data.by_utm_campaign} testid={`attribution-by-campaign-${windowKey}`} />
      </div>
    </div>
  );
}

export default function AdminAttribution() {
  const [summary, setSummary] = useState(null);
  const [activeWindow, setActiveWindow] = useState("30");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/attribution-summary")
      .then((r) => setSummary(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Failed to load attribution summary"));
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-attribution-page">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <Megaphone className="h-4 w-4 text-emerald-400" />
          <div className="text-[10px] uppercase tracking-widest text-emerald-400">Admin</div>
        </div>
        <h1 className="font-heading text-4xl tracking-tight">Signup Attribution</h1>
        <p className="text-sm text-slate-400 mt-1">
          Counts only, by first-touch campaign source. No emails or names are shown here.
        </p>
      </header>

      {error && (
        <div className="ae-card p-4 border-rose-500/30 text-sm text-rose-300" data-testid="attribution-error">
          {error}
        </div>
      )}

      {!summary && !error && (
        <div className="text-sm text-slate-500" data-testid="attribution-loading">Loading…</div>
      )}

      {summary && (
        <>
          <div className="inline-flex items-center bg-[#0A0D12] border border-white/8 rounded-sm p-1" data-testid="attribution-window-toggle">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setActiveWindow(w.key)}
                data-testid={`attribution-window-tab-${w.key}`}
                className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-sm transition-colors ${
                  activeWindow === w.key ? "bg-emerald-500 text-black" : "text-slate-400 hover:text-slate-100"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <WindowPanel data={summary[activeWindow]} windowKey={activeWindow} />
        </>
      )}
    </div>
  );
}
